/**
 * Parse checkout return query params from HashRouter (#/path?...) and regular search.
 * Billing cycle must stay in sync with llm_server/deployment_package/plan_slug_catalog.py
 */

export const CHECKOUT_BILLING_STORAGE_KEY = 'checkout_billing_cycle';
export const CHECKOUT_SESSION_STORAGE_KEY = 'checkout_session_id';
export const CHECKOUT_PLAN_STORAGE_KEY = 'checkout_plan_id';

/** @returns {'monthly'|'yearly'} */
export function normalizeBillingCycle(value) {
  const cycle = String(value || 'monthly').toLowerCase().trim();
  if (['year', 'yearly', 'annual', 'annually', '12m', '12_month'].includes(cycle)) {
    return 'yearly';
  }
  return 'monthly';
}

export function storeCheckoutContext({ sessionId, planId, billingCycle } = {}) {
  try {
    if (sessionId) {
      sessionStorage.setItem(CHECKOUT_SESSION_STORAGE_KEY, sessionId);
    }
    if (planId) {
      sessionStorage.setItem(CHECKOUT_PLAN_STORAGE_KEY, planId);
    }
    if (billingCycle) {
      sessionStorage.setItem(
        CHECKOUT_BILLING_STORAGE_KEY,
        normalizeBillingCycle(billingCycle)
      );
    }
  } catch (_) {
    /* ignore */
  }
}

export function getStoredCheckoutBillingCycle() {
  try {
    const stored = sessionStorage.getItem(CHECKOUT_BILLING_STORAGE_KEY);
    return stored ? normalizeBillingCycle(stored) : null;
  } catch (_) {
    return null;
  }
}

export function clearStoredCheckoutContext() {
  try {
    sessionStorage.removeItem(CHECKOUT_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(CHECKOUT_PLAN_STORAGE_KEY);
    sessionStorage.removeItem(CHECKOUT_BILLING_STORAGE_KEY);
    sessionStorage.removeItem('checkout_paid');
  } catch (_) {
    /* ignore */
  }
}

export function getCheckoutReturnParams() {
  const urlParams = new URLSearchParams(window.location.search);
  const hashSearch = window.location.hash.includes('?')
    ? new URLSearchParams(window.location.hash.split('?')[1])
    : new URLSearchParams('');

  const get = (key) => urlParams.get(key) || hashSearch.get(key);

  const transactionId = get('transaction_id') || get('checkout_id');
  const paddleTxn = get('_ptxn');
  const sessionId = get('session_id');
  const planId = get('plan_id') || get('plan');
  const customerEmail = get('customer_email');
  const success = get('success') === 'true';
  const billingCycle = normalizeBillingCycle(
    get('billing_cycle') ||
      get('billing_interval') ||
      get('interval') ||
      getStoredCheckoutBillingCycle() ||
      'monthly'
  );
  const effectiveTxn = transactionId || paddleTxn;

  return {
    transactionId: effectiveTxn,
    sessionId,
    /** Prefer Paddle txn_ / _ptxn / Stripe cs_ — used for verify + idempotency lock */
    checkoutId: effectiveTxn || sessionId,
    planId,
    customerEmail,
    success,
    billingCycle,
    hasCheckoutReturn: Boolean(
      (effectiveTxn || sessionId) && (success || effectiveTxn || sessionId || customerEmail)
    ),
  };
}

/** Strip checkout params from URL after processing (HashRouter-safe). */
export function clearCheckoutReturnParamsFromUrl() {
  const cleanHash = window.location.hash ? window.location.hash.split('?')[0] : '';
  const cleanSearch = window.location.search
    .replace(
      /[?&](session_id|transaction_id|checkout_id|plan_id|plan|success|billing_cycle|billing_interval|interval|_ptxn)=[^&]*/g,
      ''
    )
    .replace(/\?&/, '?')
    .replace(/\?$/, '');
  window.history.replaceState({}, document.title, window.location.pathname + cleanSearch + cleanHash);
}